import type { MouseEventHandler } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link as MuiLink, Skeleton, Stack, Typography } from '@mui/material';

import { checkedAtLine } from '../checkedAt';
import {
  CHECK_AGAIN_LABEL,
  EMPTY_STATE_COPY,
  EMPTY_STATE_TITLE,
  FINE_PRINT,
  LOAD_ERROR_COPY,
  RETRY_LABEL,
  SUBMIT_FIRST_CLAIM_LABEL,
  unavailableClaimCheckNotice,
} from '../copy';
import { BILLING_PATH, OPEN_IN_STEDI_LABEL, REVIEW_CLAIMS_LABEL, stediLinkTarget } from '../links';
import type { FoldedPayer, FoldResult, PayerAudience } from '../types';

import { Headline } from './Headline';
import { NeedsYouBanner, ReadyBanner } from './PayerBanners';
import { PayerTable } from './PayerTable';

export interface ClinicPayerConnectionsViewProps {
  status: 'loading' | 'error' | 'ready';
  /** False when the org has no Stedi API key — AC-11's empty state, not an error. */
  connected: boolean;
  /** Raw enrollment row count. AC-11 keys off rows, not folded payers. */
  rowCount: number;
  /**
   * Null while the scoped enrollment response has not arrived.
   * Renders as the skeleton, never as rows: 0 claims is the exact value that
   * holds a fully-`LIVE` payer at Ready to bill, so folding against a
   * placeholder zero would show a "Review claims" link for a payer that
   * is already live.
   */
  fold: FoldResult | null;
  /** ISO timestamp from the passthrough's `checked_at`. */
  checkedAt: string | null;
  onRetry: () => void;
  onStediLinkClick: (payer: FoldedPayer) => void;
  onFirstClaimLinkClick: () => void;
  /** Staff previews explain claim review when no clinic-scoped destination exists. */
  claimReviewNotice?: string;
  audience?: PayerAudience;
  /** Staff-only navigation to the selected organization's practice intake form. */
  intakeReviewPath?: string;
  onReviewIntakeClick?: MouseEventHandler<HTMLAnchorElement>;
  /** Injected so the "today" branch of the AC-2 line is deterministic in tests and stories. */
  now?: Date;
}

const linkSx = { fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' } as const;

/**
 * The clinic Payer Connections body (AC-2, AC-5 to AC-11).
 *
 * Pure props in, no data fetching — `ClinicPayerConnections` owns the reads
 * and the events. Every state the section can be in is a story.
 */
export function ClinicPayerConnectionsView({
  status,
  connected,
  rowCount,
  fold,
  checkedAt,
  onRetry,
  onStediLinkClick,
  onFirstClaimLinkClick,
  claimReviewNotice,
  audience,
  intakeReviewPath,
  onReviewIntakeClick,
  now,
}: ClinicPayerConnectionsViewProps) {
  // A default argument would be re-evaluated per render; this is read once
  // per render either way, and callers that care pass an explicit `now`.
  const statusAsOf = now ?? new Date();
  const freshnessLine = checkedAtLine(checkedAt, statusAsOf);

  // A failed enrollment read must never sit under a skeleton.
  if (status === 'error') {
    return (
      <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }} role="alert">
        <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>{LOAD_ERROR_COPY}</Typography>
        <Button size="small" variant="outlined" onClick={onRetry}>
          {RETRY_LABEL}
        </Button>
      </Stack>
    );
  }

  // A missing response is loading, not an empty organization. Receipt errors
  // inside a response remain visible as unknown evidence.
  if (status === 'loading' || fold === null) {
    return (
      <Stack spacing={1.5} data-testid="payer-connections-skeleton">
        <Skeleton variant="text" width={280} height={34} />
        <Skeleton variant="text" width={360} height={20} />
        <Skeleton variant="rounded" height={32} />
        <Skeleton variant="rounded" height={210} />
      </Stack>
    );
  }

  // AC-11: an organization with no Stedi key, or with no enrollment rows at
  // all, sees the setup copy and no link. Keyed on raw rows rather than
  // folded payers — an org whose every payer is Live has zero rows to show
  // but is not "being set up", and falls through to the table below.
  if (!connected || rowCount === 0) {
    return (
      <Stack spacing={1.5}>
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e2ea',
            borderRadius: '10px',
            px: '26px',
            py: '30px',
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1c1c22' }}>
            {EMPTY_STATE_TITLE}
          </Typography>
          <Typography
            sx={{ fontSize: '13px', lineHeight: 1.6, color: '#6b6b78', mt: '5px', maxWidth: 560 }}
          >
            {EMPTY_STATE_COPY}
          </Typography>
        </Box>
        {freshnessLine ? <FreshnessLine text={freshnessLine} /> : null}
      </Stack>
    );
  }

  // The confirmed-ready payers, i.e. the rows that offer a first-claim action.
  const readyPayers = fold.payers.filter(
    payer => payer.state === 'ready' && payer.hasReceivedClaim === false,
  );

  return (
    <Stack spacing={2}>
      <Headline
        counts={{ ...fold.counts, ready: fold.readyCount }}
        readyCount={fold.readyCount}
        enrollingCount={fold.enrollingCount}
        liveCount={fold.liveCount}
        audience={audience}
        unknownCount={fold.unknownCount}
        rightSlot={freshnessLine ? <FreshnessLine text={freshnessLine} /> : undefined}
      />
      {fold.counts.needs_customer > 0 && (
        <NeedsYouBanner count={fold.counts.needs_customer} audience={audience} />
      )}
      {readyPayers.length > 0 && (
        <ReadyBanner
          payerNames={readyPayers.map(payer => payer.payerName)}
          audience={audience}
          action={
            claimReviewNotice ? undefined : (
              <Button
                component={RouterLink}
                to={BILLING_PATH}
                onClick={onFirstClaimLinkClick}
                sx={{
                  backgroundColor: '#15803d',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  px: '18px',
                  py: '10px',
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#116631' },
                }}
              >
                {SUBMIT_FIRST_CLAIM_LABEL}
              </Button>
            )
          }
        />
      )}
      <PayerTable
        payers={fold.payers}
        liveCount={fold.liveCount}
        audience={audience}
        unknownCount={fold.unknownCount}
        asOf={statusAsOf}
        renderAction={payer => {
          if (audience === 'staff' && payer.state === 'needs_us') {
            return (
              <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                <MuiLink
                  href={stediLinkTarget(payer)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onStediLinkClick(payer)}
                  sx={linkSx}
                >
                  Review in Stedi
                </MuiLink>
                {intakeReviewPath && (
                  <MuiLink
                    component={RouterLink}
                    to={intakeReviewPath}
                    onClick={onReviewIntakeClick}
                    sx={linkSx}
                  >
                    Review intake details
                  </MuiLink>
                )}
              </Stack>
            );
          }
          // Clinic actions remain limited to Needs-you and Ready-to-bill rows.
          if (payer.state === 'needs_customer') {
            return (
              <MuiLink
                href={stediLinkTarget(payer)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onStediLinkClick(payer)}
                sx={linkSx}
              >
                {OPEN_IN_STEDI_LABEL}
              </MuiLink>
            );
          }
          if (payer.state === 'ready' && payer.hasReceivedClaim === false) {
            if (claimReviewNotice) {
              return (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {claimReviewNotice}
                </Typography>
              );
            }
            return (
              <MuiLink
                component={RouterLink}
                to={BILLING_PATH}
                onClick={onFirstClaimLinkClick}
                sx={linkSx}
              >
                {REVIEW_CLAIMS_LABEL}
              </MuiLink>
            );
          }
          return null;
        }}
      />
      {fold.unknownCount > 0 && (
        <Box role="status" sx={{ color: 'text.secondary', fontSize: '13px' }}>
          <Typography sx={{ fontSize: 'inherit' }}>
            {unavailableClaimCheckNotice(
              fold.payers
                .filter(payer => payer.hasReceivedClaim === null && payer.state !== 'canceled')
                .map(payer => payer.payerName),
            )}
          </Typography>
          <Button size="small" onClick={onRetry}>
            {CHECK_AGAIN_LABEL}
          </Button>
        </Box>
      )}
      <Typography sx={{ fontSize: '12px', lineHeight: 1.6, color: '#9a9aa6', maxWidth: 720 }}>
        {FINE_PRINT}
      </Typography>
    </Stack>
  );
}

function FreshnessLine({ text }: { text: string }) {
  return (
    <Typography sx={{ fontSize: '12px', color: '#9a9aa6', whiteSpace: 'nowrap' }}>{text}</Typography>
  );
}
